import { FC, useEffect, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Context } from "boomform";
import {
    getCurrentUserRequest,
    initUserRequest,
    initUserActivationRequest,
    migrateUserRequest,
} from "./../store/auth/actionCreators";
import {
    getFormRequest,
    getFormsRequest,
    createFormRequest,
    setTransferIsLoading,
} from "./../store/forms/actionCreators";

import { selectAuth } from "./../store/auth/selectors";
import { selectForms } from "./../store/forms/selectors";

import useShalowSelector from "./../hooks/useShalowSelector";
import useCookies from "../hooks/useCookies";

import Layout from "./Layout";

import { getUrlParams } from "./../helpers/getUrlParams";
import { RouteNames } from "./routes/types";
import { useFonts } from "./../hooks/useFonts";
import { confirmStripeAuthRequest } from "../store/payment/actionCreators";
import { debounce } from "../helpers/debounce";
import { toast } from "react-toastify";
import TransferForm from "./TransferPage";
// import ChatBot from "./ChatBot";

const BoomformAllStateDev = () => {
    const data = useContext(Context);
    console.log("BoomForm State: ", data);
    return null;
};

enum IHaveTryed {
    NOTHING,
    GET_FORM_BY_ID,
    GET_ALL_FORMS,
    EVERYTHING,
    DONE,
}

let haveTryed: IHaveTryed = getUrlParams().form_id ? IHaveTryed.NOTHING : IHaveTryed.GET_FORM_BY_ID;

const App: FC = () => {
    const [isTransfer, setIsTransfer] = useState<boolean>(window.location.pathname === "/transfer");
    const [transferInfo, setTransferInfo] = useState({
        instance: "",
        compId: "",
    });
    const dispatch = useDispatch();
    useFonts();
    const { getTokens } = useCookies();
    const auth = useShalowSelector(selectAuth);
    const forms = useShalowSelector(selectForms);
    const navigate = useNavigate();
    const isConfirm = window.location.pathname === "/auth/confirm";
    const isStripeAuth = window.location.pathname === "/forms/stripe/auth/callback";
    const isGoogleLogin = window.location.pathname === "/google";
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code");

    useEffect(() => {
        if (isStripeAuth && forms.current?._id) {
            const searchParams = new URLSearchParams(window.location.search);
            const code = searchParams.get("code");
            const queries = `form_Id=${forms.current._id}&code=${code}`;
            if (code === null) {
                navigate(`/${forms.current._id}/${RouteNames.Payment}`);
                return;
            }
            dispatch(confirmStripeAuthRequest({ queries }));
            navigate(`/${forms.current._id}/${RouteNames.Payment}`);
        }
        // eslint-disable-next-line
    }, [isStripeAuth, forms.current?._id]);

    useEffect(() => {
        if (isGoogleLogin) {
            const currentFormId = localStorage.getItem("authFormId");
            const searchParams = new URLSearchParams(window.location.search);
            const success = searchParams.get("success");

            if (success) {
                const token: string = searchParams.get("token")!;
                const refreshToken: string = searchParams.get("refresh_token")!;
                dispatch(migrateUserRequest({ userId: token, userRefreshToken: refreshToken, formId: currentFormId! }));
                return;
            }
            toast("Something went wrong . Please try again");
            navigate(`/${currentFormId}/${RouteNames.Edit}`);
        }
        // eslint-disable-next-line
    }, [isGoogleLogin, forms.current?._id]);

    useEffect(() => {
        const theme = window.matchMedia("(prefers-color-scheme:light)").matches;
        const iosFavicon = document.getElementById("iosFavicon");
        const favicon = document.getElementById("favicon");

        if (theme) {
            iosFavicon?.setAttribute("href", `${process.env.PUBLIC_URL}/lightLogo.png`);
            favicon?.setAttribute("href", `${process.env.PUBLIC_URL}/lightLogo.png`);
        } else {
            iosFavicon?.setAttribute("href", `${process.env.PUBLIC_URL}/darkLogo.png`);
            favicon?.setAttribute("href", `${process.env.PUBLIC_URL}/darkLogo.png`);
        }
    }, []);

    useEffect(() => {
        const { accessToken, refreshToken } = getTokens();
        if (accessToken && refreshToken) {
            localStorage.setItem("token", accessToken);
            localStorage.setItem("refresh_token", refreshToken);
        }
        debounce(
            "get_current_user",
            () => {
                dispatch(getCurrentUserRequest({}));
            },
            0,
        );
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isConfirm) dispatch(initUserActivationRequest({ code }));

        dispatch(initUserRequest({}));
    }, [dispatch, isConfirm, code]);

    useEffect(() => {
        if (isConfirm || isTransfer || isGoogleLogin) return;

        if (auth.token && ((forms.current === null && !forms.isLoading) || haveTryed === IHaveTryed.NOTHING))
            switch (haveTryed) {
                case IHaveTryed.NOTHING:
                    const params = getUrlParams();
                    if (params?.form_id !== null) dispatch(getFormRequest({ id: params?.form_id }));

                    haveTryed = IHaveTryed.GET_FORM_BY_ID;
                    break;

                case IHaveTryed.GET_FORM_BY_ID:
                    dispatch(getFormsRequest({}));
                    haveTryed = IHaveTryed.GET_ALL_FORMS;

                    break;

                case IHaveTryed.GET_ALL_FORMS:
                    dispatch(createFormRequest({ name: "Untitled Form" }));

                    haveTryed = IHaveTryed.EVERYTHING;
                    break;

                case IHaveTryed.EVERYTHING:
                    // Need to throw some error
                    break;
            }
        else if (auth.token && forms.current !== null) haveTryed = IHaveTryed.DONE;
        //eslint-disable-next-line
    }, [dispatch, auth, forms, isConfirm]);

    useEffect(() => {
        if (isTransfer) {
            const searchParams = new URLSearchParams(window.location.search);
            window.history.replaceState(null, "", window.location.origin);
            const instance = searchParams.get("instance")!;
            const compId = searchParams.get("compId")!;
            setTransferInfo({
                instance,
                compId,
            });
            dispatch(setTransferIsLoading({ isLoading: true }));
        }
        //eslint-disable-next-line
    }, [isTransfer]);

    useEffect(() => {
        if (isConfirm) return;

        if (forms.current !== null) {
            let params = getUrlParams();
            let { form_id, path } = params;

            if (form_id !== forms.current._id) form_id = forms.current._id;
            if (!Object.values(RouteNames).includes(path as RouteNames)) path = RouteNames.Edit;

            if (path !== params.path || form_id !== params.form_id) navigate(`/${form_id}/${path}`);
        }
    }, [forms, navigate, isConfirm]);

    // if ((auth.errors !== null || forms.error !== null) && haveTryed === IHaveTryed.DONE)
    //     return <>Something went wrong</>;
    if (isTransfer) return <TransferForm transferInfo={transferInfo} setIsTransfer={setIsTransfer} />;
    if (auth === null || forms.current === null) return null;

    return (
        <>
            <Layout form={forms.current} />
            {/* <ChatBot /> */}
            <BoomformAllStateDev />
        </>
    );
};

export default App;
